import React, { memo } from 'react';
import type { CalendarDay } from '../../hooks/useCalendarData';
import type { WageType, AbsenceType } from '../../types';
import { isBlockedCompanyVacation } from '../../utils/companyVacation';

interface DayCellProps {
  day: CalendarDay;
  isFocused?: boolean;
  onClick: (day: CalendarDay) => void;
  onDoubleClick?: (day: CalendarDay) => void;
  /** WageType des betrachteten Users — bestimmt ob Betriebsferien klickbar sind. null/undefined = HOURLY (klickbar). */
  viewedUserWageType?: WageType | null;
  /** Urlaubsstunden sind vorläufig berechnet (zukünftiger Urlaubstag) */
  isProvisional?: boolean;
  /** Vorläufig berechnete Urlaubsstunden (13-Wochen-Durchschnitt) */
  provisionalHours?: number;
  /** AbsenceType-Lookup nach ID */
  absenceTypeById?: (id: string) => AbsenceType | undefined;
  /** Vorgemerkte Abwesenheit für diesen Tag (Schraffur-Overlay). Wirkt nur wenn kein realer Eintrag existiert. */
  plannedAbsence?: { absenceType?: AbsenceType; requestId: string };
}

export const DayCell = memo(function DayCell({ day, isFocused, onClick, onDoubleClick, viewedUserWageType, isProvisional, provisionalHours, absenceTypeById, plannedAbsence }: DayCellProps) {
  const { dayOfMonth, isWeekend, isToday, entry } = day;

  // Benutzerdefinierter AbsenceType (nicht-System) für Farbe/Icon
  const absenceType = entry?.absenceTypeId && absenceTypeById
    ? absenceTypeById(entry.absenceTypeId)
    : undefined;
  const isCustomAbsenceType = absenceType && !absenceType.isSystem;

  // DSGVO-Sicht (docs/ABSENCE_VISIBILITY.md): Wenn das Backend bei Manager-Sicht
  // auf einen OCCUPIED_ONLY-Typ den absenceTypeId verbirgt, kommt der Eintrag
  // mit isAbsence=true und absenceTypeId=null an. Wir rendern dann einen
  // neutralen „abwesend"-Block ohne Typ-Hinweis.
  const isOccupiedOnly = entry?.isAbsence === true && !entry?.absenceTypeId;

  const isCompanyVacation = entry?.isCompanyVacation;
  // Betriebsferien sind nur bei FIXED-Usern geblockt. HOURLY (oder null/undefined) = klickbar.
  const isBlocked = isBlockedCompanyVacation(isCompanyVacation, viewedUserWageType);
  const isEditableCompanyVacation = isCompanyVacation && !isBlocked;
  const hasManagerNote = !!entry?.managerNote;

  const getClassName = (): string => {
    const base = 'day-cell';
    const classes = [base];

    if (isFocused) {
      classes.push('ring-2 ring-gray-900 dark:ring-gray-100 ring-offset-1');
    }

    if (isToday) {
      classes.push('day-today');
    }

    // Manager-Anmerkung hat höchste Priorität bei der visuellen Hervorhebung
    if (hasManagerNote) {
      classes.push('ring-2 ring-blue-400 dark:ring-blue-500 ring-offset-2');
    }

    if (isCompanyVacation) {
      classes.push('day-company-vacation');
      // HOURLY-Betriebsferien: Klickbar (Urlaub/Krankheit eintragbar)
      if (isEditableCompanyVacation) {
        classes.push('!cursor-pointer');
      }
    } else if (isOccupiedOnly) {
      // DSGVO-Sicht: typ-verborgener Abwesenheits-Tag (Manager-View auf
      // Krankheit, Mutterschutz, Reha o.ae.). Bewusst farblos.
      classes.push('day-absence-neutral');
    } else if (isCustomAbsenceType) {
      // Benutzerdefinierter Typ — dynamische Farbe
      classes.push(`day-absence-${absenceType.color}`);
    } else if (entry?.isSickDay) {
      classes.push('day-sickday');
    } else if (entry?.isVacation) {
      classes.push('day-vacation');
      if (isProvisional) classes.push('border-dashed');
    } else if (entry?.isHalfVacation) {
      classes.push('day-half-vacation');
      if (isProvisional) classes.push('border-dashed');
    } else if (entry?.isHoliday) {
      classes.push('day-holiday');
    } else if (entry?.hours && entry.hours > 0) {
      classes.push('day-workday');
    } else if (isWeekend) {
      classes.push('day-weekend');
    }

    // Vorgemerkte Abwesenheit als Overlay (nur ohne echten Eintrag).
    if (!entry && plannedAbsence) {
      classes.push('day-planned');
    }

    return classes.join(' ');
  };

  const handleClick = () => {
    // Betriebsferien bei FIXED-Usern gesperrt, bei HOURLY klickbar
    if (isBlocked) return;
    onClick(day);
  };

  const handleDoubleClick = () => {
    // Doppelklick (Quick-Toggle Stunden) gesperrt bei geblockten Betriebsferien
    if (isBlocked) return;
    if (onDoubleClick) {
      onDoubleClick(day);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isBlocked) return;
      onClick(day);
    }
  };

  const germanDate = new Date(day.dateString).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  });

  const companyVacationLabel = isEditableCompanyVacation
    ? ' - Betriebsferien'
    : isCompanyVacation
      ? ' - Betriebsferien (gesperrt)'
      : '';

  const provisionalLabel = isProvisional ? ' - Vorläufig (wird mit neuen Arbeitsdaten aktualisiert)' : '';

  const hoursLabel = entry?.hours
    ? ` - ${entry.hours}h`
    : provisionalHours && provisionalHours > 0
      ? ` - ~${provisionalHours.toLocaleString('de-DE', { maximumFractionDigits: 1 })}h (vorläufig)`
      : '';

  const plannedLabel = !entry && plannedAbsence
    ? ` - Geplant${plannedAbsence.absenceType ? `: ${plannedAbsence.absenceType.name}` : ''}`
    : '';

  // Bei isOccupiedOnly: bewusst nur „abwesend", keine Typ- oder Note-Details,
  // weil Hover-Tooltip sonst aus Versehen den Grund preisgeben koennte.
  const title = entry
    ? isOccupiedOnly
      ? `${germanDate} - abwesend`
      : `${germanDate}${companyVacationLabel}${provisionalLabel}${entry.isSickDay ? ' - Krankheit' : ''}${hoursLabel}${entry.note ? ` - ${entry.note}` : ''}${entry.managerNote ? ` - Manager-Anmerkung: ${entry.managerNote}` : ''}`
    : `${germanDate}${plannedLabel}`;

  return (
    <button
      className={getClassName()}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      title={title}
      aria-label={`Tag bearbeiten: ${new Date(day.dateString).toLocaleDateString('de-DE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}`}
    >
      <div className="flex flex-col items-center justify-center h-full gap-0.5">
        {/* Datum und Icons in einer Zeile */}
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium leading-tight">{dayOfMonth}</span>
          {hasManagerNote && (
            <span className="text-[10px] leading-none" title={entry.managerNote}>
              📝
            </span>
          )}
          {!entry && plannedAbsence && (
            <span className="text-[10px] leading-none" title={`Geplant${plannedAbsence.absenceType ? `: ${plannedAbsence.absenceType.name}` : ''}`}>
              {plannedAbsence.absenceType?.icon || '📋'}
            </span>
          )}
          {isCustomAbsenceType && absenceType.icon ? (
            <span className="text-[10px] leading-none" title={absenceType.name}>{absenceType.icon}</span>
          ) : (
            <>
              {entry?.isVacation && (
                <span className="text-[10px] leading-none" title="Urlaub">🏖️</span>
              )}
              {entry?.isHalfVacation && (
                <span className="text-[10px] leading-none" title="Halber Urlaubstag">½🏖️</span>
              )}
              {entry?.isSickDay && (
                <span className="text-[10px] leading-none" title="Krankheitstag">🤒</span>
              )}
            </>
          )}
        </div>

        {/* Arbeitsstunden in separater Zeile */}
        {entry?.hours && entry.hours > 0 ? (
          <span className="text-[9px] font-medium tabular-nums text-gray-700 dark:text-gray-200 leading-none">
            {entry.hours}h
          </span>
        ) : provisionalHours && provisionalHours > 0 ? (
          <span className="text-[9px] font-medium tabular-nums text-muted-foreground italic leading-none">
            {provisionalHours.toLocaleString('de-DE', { maximumFractionDigits: 1 })}*
          </span>
        ) : null}
      </div>
    </button>
  );
});
